import { useCallback, useEffect, useRef, useState } from 'react';
import type { HotelSearchQuery } from '../api/hotels.types';
import {
  HotelSearchStreamError,
  type UseHotelSearchStreamOptions,
  useHotelSearchStream,
} from './useHotelSearchStream';
import type { HotelOfferCardModel, HotelSearchStatus } from '../types/hotels.presentation';
import { mergeHotelOffers } from '../utils/mergeHotelOffers';

interface UseHotelSearchOptions {
  streamOptions?: UseHotelSearchStreamOptions;
}

/** Backend contract: hotel results are considered fresh for 5 minutes. */
const HOTEL_RESULTS_TTL_MS = 5 * 60 * 1000;

interface UseHotelSearchState {
  status: HotelSearchStatus;
  offers: HotelOfferCardModel[];
  message?: string;
  /** Timestamp (ms) when current results expire. `undefined` before first completion, on error, or while searching. */
  expiresAt?: number;
  search: (query: HotelSearchQuery) => void;
  retry: () => void;
}

const DEFAULT_ERROR_MESSAGE = 'hotels.results.searchError';

export function useHotelSearch({ streamOptions }: UseHotelSearchOptions = {}): UseHotelSearchState {
  const [status, setStatus] = useState<HotelSearchStatus>('idle');
  const [offers, setOffers] = useState<HotelOfferCardModel[]>([]);
  const [message, setMessage] = useState<string | undefined>();
  const [expiresAt, setExpiresAt] = useState<number>();
  const lastQueryRef = useRef<HotelSearchQuery | null>(null);
  const searchIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const offerCountRef = useRef(0);
  const { searchStream } = useHotelSearchStream(streamOptions);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    [],
  );

  const search = useCallback(
    (query: HotelSearchQuery) => {
      const searchId = searchIdRef.current + 1;
      searchIdRef.current = searchId;
      lastQueryRef.current = query;
      offerCountRef.current = 0;
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setStatus('loading');
      setOffers([]);
      setMessage(undefined);
      setExpiresAt(undefined);

      searchStream(
        query,
        {
          onBatch: (cards) => {
            if (searchId !== searchIdRef.current) return;

            if (cards.length === 0) return;

            offerCountRef.current += cards.length;
            setOffers((current) => mergeHotelOffers(current, cards));
            setStatus('streaming');
          },
          onDone: () => {
            if (searchId !== searchIdRef.current) return;

            const finalStatus = offerCountRef.current > 0 ? 'success' : 'empty';
            setStatus(finalStatus);
            setExpiresAt(Date.now() + HOTEL_RESULTS_TTL_MS);
          },
        },
        abortController.signal,
      ).catch((error: unknown) => {
        if (searchId !== searchIdRef.current) {
          return;
        }

        if (abortController.signal.aborted) {
          return;
        }

        const hasPartialResults = offerCountRef.current > 0;

        if (!hasPartialResults) {
          setOffers([]);
        }

        setStatus(hasPartialResults ? 'partial-error' : 'error');
        setExpiresAt(undefined);
        setMessage(
          error instanceof HotelSearchStreamError ? error.messageKey : DEFAULT_ERROR_MESSAGE,
        );
      });
    },
    [searchStream],
  );

  const retry = useCallback(() => {
    if (lastQueryRef.current) {
      search(lastQueryRef.current);
    }
  }, [search]);

  return { status, offers, message, expiresAt, search, retry };
}
